import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Filter, Search, X } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { ProductCard } from '../components/ProductCard';
import { CategoryIcon } from '../components/CategoryIcon';
import { SubcategoryIcon } from '../components/SubcategoryIcon';
import {
  categories,
  normalizeProductCategory,
  normalizeProductSubcategory,
  subcategoryOptionsByCategory,
} from '../data/products';
import { useProducts } from '../hooks/useProducts';

export default function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { products: productsList } = useProducts();

  const categoryParam = normalizeProductCategory(searchParams.get('category'));
  const subParam = normalizeProductSubcategory(categoryParam, searchParams.get('sub'));

  const [selectedCategory, setSelectedCategory] = useState(categoryParam || 'all');
  const [selectedSubcategory, setSelectedSubcategory] = useState(subParam || 'all');

  const scrollToTop = () => {
    if (typeof window === 'undefined') return;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    setSelectedCategory(categoryParam || 'all');
    setSelectedSubcategory(subParam || 'all');
  }, [categoryParam, subParam]);


  const currentSubcategories =
    selectedCategory !== 'all' ? subcategoryOptionsByCategory[selectedCategory] || [] : [];

  const filteredProducts = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    return productsList.filter((product) => {
      if (product.isActive === false) return false;
      const productCategory = normalizeProductCategory(product.category);
      if (selectedCategory !== 'all' && productCategory !== selectedCategory) return false;
      if (selectedCategory !== 'all' && selectedSubcategory !== 'all') {
        const productSubcategory = normalizeProductSubcategory(productCategory, product.subcategory);
        if (productSubcategory !== selectedSubcategory) return false;
      }
      if (normalizedQuery) {
        const haystack = [
          product.name,
          product.shortDescription,
          product.longDescription,
          product.ingredients?.join(' '),
          product.flavorNotes?.join(' '),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(normalizedQuery)) return false;
      }
      return true;
    });
  }, [productsList, searchQuery, selectedCategory, selectedSubcategory]);

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setSelectedSubcategory('all');
    const next = new URLSearchParams(searchParams);
    if (category === 'all') {
      next.delete('category');
      next.delete('sub');
    } else {
      next.set('category', category);
      next.delete('sub');
    }
    setSearchParams(next);
  };

  const handleSubcategoryChange = (subcategory) => {
    setSelectedSubcategory(subcategory);
    const next = new URLSearchParams(searchParams);
    if (subcategory === 'all') {
      next.delete('sub');
    } else {
      next.set('sub', subcategory);
    }
    setSearchParams(next);
    scrollToTop();
  };

  const clearFilters = () => {
    setSelectedCategory('all');
    setSelectedSubcategory('all');
    setSearchQuery('');
    setSearchParams({});
  };

  const hasActiveFilters =
    selectedCategory !== 'all' ||
    selectedSubcategory !== 'all' ||
    searchQuery.trim().length > 0;

  const getPageTitle = () => {
    if (selectedCategory !== 'all' && selectedSubcategory !== 'all') {
      const subs = subcategoryOptionsByCategory[selectedCategory];
      const sub = subs?.find((s) => s.id === selectedSubcategory);
      return sub?.name || categories.find((c) => c.id === selectedCategory)?.name || 'Termékek';
    }
    if (selectedCategory === 'all') return 'Összes termék';
    return categories.find((c) => c.id === selectedCategory)?.name || 'Termékek';
  };

  return (
    <Layout>
      <div className="pt-8 pb-16 md:pb-20">
        <div className="container-zinga">
          <div className="mb-10 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h1 className="font-display text-4xl md:text-5xl font-bold">{getPageTitle()}</h1>
              <p className="mt-4 text-muted-foreground">{filteredProducts.length} termék</p>
            </div>
            <div className="w-full lg:max-w-xs">
              <label htmlFor="product-search" className="block text-sm font-medium mb-2">
                Keresés
              </label>
              <div className="relative">
                <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  id="product-search"
                  type="text"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="Keress terméket"
                  className="w-full rounded-2xl border border-border bg-card py-2.5 pl-10 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-secondary"
                    aria-label="Keresés törlése"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-col lg:flex-row gap-8">
            <aside className="hidden lg:block w-64 flex-shrink-0">
              <div className="sticky top-24 space-y-8">
                <FilterContent
                  selectedCategory={selectedCategory}
                  selectedSubcategory={selectedSubcategory}
                  currentSubcategories={currentSubcategories}
                  onCategoryChange={handleCategoryChange}
                  onSubcategoryChange={handleSubcategoryChange}
                  onClear={clearFilters}
                  hasActiveFilters={hasActiveFilters}
                />
              </div>
            </aside>

            <div className="lg:hidden flex items-center gap-4">
              <button
                onClick={() => setShowFilters(true)}
                className="flex items-center gap-2 px-4 py-2 bg-card rounded-xl border border-border"
              >
                <Filter className="w-4 h-4" />
                Szűrők
                {hasActiveFilters && <span className="w-2 h-2 bg-primary rounded-full" />}
              </button>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="text-sm text-muted-foreground hover:text-foreground"
                >
                  Összes törlése
                </button>
              )}
            </div>

            {showFilters && (
              <div className="fixed inset-0 z-50 lg:hidden">
                <div
                  className="absolute inset-0 bg-foreground/20 backdrop-blur-sm"
                  onClick={() => setShowFilters(false)}
                />
                <div className="absolute right-0 top-0 bottom-0 w-80 max-w-full bg-background p-6 overflow-y-auto animate-fade-in">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="font-display text-xl font-semibold">Szűrők</h2>
                    <button
                      onClick={() => setShowFilters(false)}
                      className="p-2 hover:bg-secondary rounded-xl"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  <FilterContent
                    selectedCategory={selectedCategory}
                    selectedSubcategory={selectedSubcategory}
                    currentSubcategories={currentSubcategories}
                    onCategoryChange={handleCategoryChange}
                    onSubcategoryChange={handleSubcategoryChange}
                    onClear={clearFilters}
                    hasActiveFilters={hasActiveFilters}
                  />
                </div>
              </div>
            )}

            <div className="flex-1">
              {filteredProducts.length === 0 ? (
                <div className="text-center py-16">
                  <p className="text-muted-foreground mb-4">Nem található termék ezekkel a szűrőkkel.</p>
                  <button onClick={clearFilters} className="btn-secondary">
                    Szűrők törlése
                  </button>
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6">
                  {filteredProducts.map((product) => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

function FilterContent({
  selectedCategory,
  selectedSubcategory,
  currentSubcategories,
  onCategoryChange,
  onSubcategoryChange,
  onClear,
  hasActiveFilters,
}) {
  return (
    <>
      <div>
        <h3 className="font-display font-semibold mb-4">Kategória</h3>
        <div className="space-y-2">
          <FilterButton active={selectedCategory === 'all'} onClick={() => onCategoryChange('all')}>
            Összes termék
          </FilterButton>
          {categories.map((category) => (
            <FilterButton
              key={category.id}
              active={selectedCategory === category.id}
              onClick={() => onCategoryChange(category.id)}
            >
              <span className="flex items-center gap-2.5">
                <CategoryIcon categoryId={category.id} compact />
                <span>{category.name}</span>
              </span>
            </FilterButton>
          ))}
        </div>
      </div>

      {currentSubcategories.length > 0 && (
        <div>
          <h3 className="font-display font-semibold mb-4">Alkategória</h3>
          <div className="space-y-2">
            <FilterButton active={selectedSubcategory === 'all'} onClick={() => onSubcategoryChange('all')}>
              Összes
            </FilterButton>
            {currentSubcategories.map((sub) => (
              <FilterButton
                key={sub.id}
                active={selectedSubcategory === sub.id}
                onClick={() => onSubcategoryChange(sub.id)}
              >
                <span className="flex items-center gap-2.5">
                  <SubcategoryIcon subcategoryId={sub.id} compact />
                  <span>{sub.name}</span>
                </span>
              </FilterButton>
            ))}
          </div>
        </div>
      )}

      {hasActiveFilters && (
        <button onClick={onClear} className="w-full btn-secondary text-sm">
          Összes szűrő törlése
        </button>
      )}
    </>
  );
}

function FilterButton({ children, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-4 py-2 rounded-xl text-sm transition-colors ${
        active ? 'bg-primary text-primary-foreground' : 'hover:bg-secondary'
      }`}
    >
      {children}
    </button>
  );
}
